<template>
  <div>
    <!-- Header with Tabs -->
    <v-tabs v-model="activeTab" class="mb-4">
      <v-tab>Timeline View</v-tab>
      <v-tab>Analysis View</v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab">
      <!-- Timeline Tab -->
      <v-tab-item>
        <!-- Category Selection -->
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedCategory"
              :items="costCategoryOptions"
              label="Select Cost Category"
              no-data-text="Not available"
              outlined
              dense
              @change="loadCategoryHistory"
            ></v-select>
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              v-model="showInactive"
              label="Include Inactive Rates"
              class="mt-2"
            ></v-switch>
          </v-col>
          <v-col cols="12" md="4">
            <v-switch
              v-model="showVersioning"
              label="Show Version Details"
              class="mt-2"
            ></v-switch>
          </v-col>
        </v-row>

    <!-- Timeline View -->
    <div v-if="selectedCategory && categoryRates.length > 0">
      <v-timeline align-top dense>
        <v-timeline-item
          v-for="(rate, index) in filteredCategoryRates"
          :key="rate.id"
          :color="getTimelineColor(rate, index)"
          :icon="getTimelineIcon(rate)"
          fill-dot
        >
          <template v-slot:opposite>
            <span class="text-caption">
              {{ formatDate(rate.effectiveFrom) }}
              <br>
              to
              <br>
              {{ formatDate(rate.effectiveTo) }}
            </span>
          </template>

          <v-card class="elevation-2">
            <v-card-title class="pb-2">
              <v-chip
                :color="getCategoryColor(rate.costCategory)"
                text-color="white"
                small
                class="mr-2"
              >
                {{ formatCostCategory(rate.costCategory) }}
              </v-chip>
              <v-chip
                :color="rate.isActive ? 'success' : 'error'"
                text-color="white"
                x-small
              >
                {{ rate.isActive ? 'Active' : 'Inactive' }}
              </v-chip>
              <v-spacer></v-spacer>
              <span class="text-h6">{{ formatRate(rate.rate, rate.rateType) }}</span>
            </v-card-title>

            <v-card-text class="pt-0">
              <v-row dense>
                <v-col cols="6">
                  <div class="text-caption grey--text">Rate Type</div>
                  <div class="text-body-2">{{ rate.rateType }}</div>
                </v-col>
                <v-col cols="6">
                  <div class="text-caption grey--text">Created By</div>
                  <div class="text-body-2">{{ rate.createdBy }}</div>
                </v-col>
                <v-col cols="12" v-if="showVersioning">
                  <div class="text-caption grey--text">Rate ID</div>
                  <div class="text-body-2 font-weight-medium">{{ rate.id }}</div>
                </v-col>
                <v-col cols="12">
                  <div class="text-caption grey--text">Created At</div>
                  <div class="text-body-2">{{ formatDateTime(rate.createdAt) }}</div>
                </v-col>
              </v-row>

              <!-- Show versioning details if enabled -->
              <div v-if="showVersioning" class="mt-2">
                <v-divider class="mb-2"></v-divider>
                <v-row dense>
                  <v-col cols="12">
                    <div class="text-caption grey--text">Version Information</div>
                    <v-chip
                      :color="getVersionColor(rate, index)"
                      text-color="white"
                      x-small
                      class="mr-1"
                    >
                      Version {{ getVersionNumber(rate, index) }}
                    </v-chip>
                    <v-chip
                      v-if="isCurrentVersion(rate)"
                      color="success"
                      text-color="white"
                      x-small
                    >
                      Current
                    </v-chip>
                  </v-col>
                </v-row>
              </div>

              <!-- Show overlapping warning if applicable -->
              <v-alert
                v-if="hasOverlap(rate, index)"
                type="warning"
                dense
                class="mt-2 mb-0"
              >
                <v-icon small class="mr-1">mdi-alert</v-icon>
                This rate period overlaps with other rates
                <div class="text-caption mt-1">
                  Overlapping with: {{ getOverlappingRateIds(rate) }}
                </div>
              </v-alert>

              <!-- Show rate change information -->
              <div v-if="index < filteredCategoryRates.length - 1" class="mt-2">
                <v-divider class="mb-2"></v-divider>
                <div class="text-caption grey--text">
                  Rate Change:
                  <span :class="getRateChangeClass(rate, filteredCategoryRates[index + 1])">
                    {{ getRateChangeText(rate, filteredCategoryRates[index + 1]) }}
                  </span>
                </div>
                <div v-if="showVersioning" class="text-caption grey--text mt-1">
                  Supersedes: Rate ID {{ filteredCategoryRates[index + 1].id }}
                </div>
              </div>

              <!-- Show effective period details -->
              <div v-if="showVersioning" class="mt-2">
                <v-divider class="mb-2"></v-divider>
                <div class="text-caption grey--text">
                  Effective Period: {{ getEffectiveDays(rate) }} days
                </div>
                <div class="text-caption grey--text">
                  Status:
                  <span :class="getEffectiveStatusClass(rate)">
                    {{ getEffectiveStatus(rate) }}
                  </span>
                </div>
              </div>
            </v-card-text>

            <v-card-actions>
              <v-btn
                text
                small
                color="primary"
                @click="viewRateDetails(rate)"
              >
                View Details
              </v-btn>
              <v-btn
                text
                small
                color="warning"
                @click="editRate(rate)"
                :disabled="!rate.isActive"
              >
                Edit
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-timeline-item>
      </v-timeline>
    </div>

    <!-- Empty State -->
    <div v-else-if="selectedCategory && categoryRates.length === 0" class="text-center py-8">
      <v-icon size="64" color="grey lighten-2">mdi-history</v-icon>
      <div class="text-h6 grey--text mt-2">No Rate History</div>
      <div class="text-body-2 grey--text">
        No rates found for {{ formatCostCategory(selectedCategory) }}
      </div>
    </div>

        <!-- Initial State -->
        <div v-else class="text-center py-8">
          <v-icon size="64" color="grey lighten-2">mdi-timeline-clock</v-icon>
          <div class="text-h6 grey--text mt-2">Select a Cost Category</div>
          <div class="text-body-2 grey--text">
            Choose a cost category above to view its rate history timeline
          </div>
        </div>
      </v-tab-item>

      <!-- Analysis Tab -->
      <v-tab-item>
        <rate-analysis-view />
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ValidationUtils } from '../../types';
import costCategoryMixin from '../../mixins/costCategoryMixin';

export default {
  name: 'RateHistoryView',
  mixins: [costCategoryMixin],
  components: {
    RateAnalysisView: () => import('./RateAnalysisView.vue'),
  },
  data() {
    return {
      activeTab: 0,
      selectedCategory: null,
      showInactive: true,
      showVersioning: false,
    };
  },
  computed: {
    ...mapGetters('rateMaster', ['allRates']),

    categoryRates() {
      if (!this.selectedCategory) return [];

      return this.allRates
        .filter((rate) => rate.costCategory === this.selectedCategory)
        .sort((a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom));
    },

    filteredCategoryRates() {
      if (this.showInactive) {
        return this.categoryRates;
      }
      return this.categoryRates.filter((rate) => rate.isActive);
    },
  },
  methods: {
    loadCategoryHistory() {
      // This method can be used to trigger additional loading if needed
      // For now, we use computed properties to reactively filter rates
    },

    getTimelineColor(rate, index) {
      if (!rate.isActive) return 'grey';
      if (this.hasOverlap(rate, index)) return 'warning';
      return this.getCategoryColor(rate.costCategory);
    },

    getTimelineIcon(rate) {
      if (!rate.isActive) return 'mdi-close-circle';
      return 'mdi-check-circle';
    },

    formatRate(rate, rateType) {
      if (rateType === 'PERCENTAGE') {
        return `${rate}%`;
      }
      return this.$formatCurrency(rate);
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    },

    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    hasOverlap(rate, index) {
      // Check if this rate overlaps with any other rate in the same category
      return this.categoryRates.some((otherRate, otherIndex) => {
        if (otherIndex === index || otherRate.id === rate.id) return false;

        return ValidationUtils.doDateRangesOverlap(
          { startDate: new Date(rate.effectiveFrom), endDate: new Date(rate.effectiveTo) },
          {
            startDate: new Date(otherRate.effectiveFrom),
            endDate: new Date(otherRate.effectiveTo),
          },
        );
      });
    },

    getOverlappingRates(rate) {
      // Get all rates that overlap with the given rate
      return this.categoryRates.filter((otherRate) => {
        if (otherRate.id === rate.id) return false;

        return ValidationUtils.doDateRangesOverlap(
          { startDate: new Date(rate.effectiveFrom), endDate: new Date(rate.effectiveTo) },
          {
            startDate: new Date(otherRate.effectiveFrom),
            endDate: new Date(otherRate.effectiveTo),
          },
        );
      });
    },

    getRateChangeText(currentRate, previousRate) {
      if (!previousRate) return '';

      const currentValue = currentRate.rate;
      const previousValue = previousRate.rate;
      const difference = currentValue - previousValue;
      const percentChange = ((difference / previousValue) * 100).toFixed(1);

      if (difference > 0) {
        return `+${Math.abs(difference).toFixed(2)} (+${percentChange}%)`;
      } if (difference < 0) {
        return `-${Math.abs(difference).toFixed(2)} (${percentChange}%)`;
      }
      return 'No change';
    },

    getRateChangeClass(currentRate, previousRate) {
      if (!previousRate) return '';

      const difference = currentRate.rate - previousRate.rate;
      if (difference > 0) return 'success--text';
      if (difference < 0) return 'error--text';
      return 'grey--text';
    },

    viewRateDetails(rate) {
      this.$emit('view-rate', rate);
    },

    editRate(rate) {
      this.$emit('edit-rate', rate);
    },

    getVersionNumber(rate, index) {
      // Version numbers are based on chronological order (newest = highest version)
      return this.filteredCategoryRates.length - index;
    },

    getVersionColor(rate, index) {
      if (this.isCurrentVersion(rate)) return 'success';
      if (index === 0) return 'primary'; // Latest version
      return 'grey';
    },

    isCurrentVersion(rate) {
      // A rate is current if it's active and its effective period includes today
      if (!rate.isActive) return false;

      const today = new Date();
      const effectiveFrom = new Date(rate.effectiveFrom);
      const effectiveTo = new Date(rate.effectiveTo);

      return today >= effectiveFrom && today <= effectiveTo;
    },

    getOverlappingRateIds(rate) {
      const overlapping = this.getOverlappingRates(rate);
      return overlapping.map((r) => `ID ${r.id}`).join(', ');
    },

    getEffectiveDays(rate) {
      const effectiveFrom = new Date(rate.effectiveFrom);
      const effectiveTo = new Date(rate.effectiveTo);
      const diffTime = Math.abs(effectiveTo - effectiveFrom);
      return Math.floor(diffTime / (1000 * 60 * 60 * 24));
    },

    getEffectiveStatus(rate) {
      const today = new Date();
      const effectiveFrom = new Date(rate.effectiveFrom);
      const effectiveTo = new Date(rate.effectiveTo);

      if (!rate.isActive) return 'Inactive';
      if (today < effectiveFrom) return 'Future';
      if (today > effectiveTo) return 'Expired';
      return 'Active';
    },

    getEffectiveStatusClass(rate) {
      const status = this.getEffectiveStatus(rate);
      switch (status) {
        case 'Active': return 'success--text';
        case 'Future': return 'info--text';
        case 'Expired': return 'warning--text';
        case 'Inactive': return 'error--text';
        default: return 'grey--text';
      }
    },
  },
};
</script>

<style scoped>
.v-timeline-item__body {
  padding-bottom: 24px;
}
</style>
